import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../common/Navigation';
import Footer from '../../common/Footer';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
  LockClosedIcon,
  CurrencyDollarIcon,
  ScaleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const TermsAndConditions: React.FC = () => {
  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
    
    // Handle hash navigation for table of contents
    if (window.location.hash) {
      setTimeout(() => {
        const element = document.querySelector(window.location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const tableOfContents = [
    { id: '1-introduction-and-acceptance', title: 'Introduction and Acceptance', icon: DocumentTextIcon },
    { id: '2-eligibility-and-access-requirements', title: 'Eligibility and Access Requirements', icon: UserGroupIcon },
    { id: '3-important-disclaimers-and-risk-factors', title: 'Important Disclaimers and Risk Factors', icon: ExclamationTriangleIcon },
    { id: '4-account-registration-and-security', title: 'Account Registration and Security', icon: LockClosedIcon },
    { id: '5-services-provided', title: 'Services Provided', icon: BuildingOfficeIcon },
    { id: '6-prohibited-uses', title: 'Prohibited Uses', icon: ShieldExclamationIcon },
    { id: '7-intellectual-property-rights', title: 'Intellectual Property Rights', icon: DocumentTextIcon },
    { id: '8-user-content-and-data', title: 'User Content and Data', icon: DocumentTextIcon },
    { id: '9-third-party-services-and-content', title: 'Third-Party Services and Content', icon: GlobeAltIcon },
    { id: '10-fees-and-payment-terms', title: 'Fees and Payment Terms', icon: CurrencyDollarIcon },
    { id: '11-confidentiality', title: 'Confidentiality', icon: LockClosedIcon },
    { id: '12-representations-and-warranties', title: 'Representations and Warranties', icon: ShieldExclamationIcon },
    { id: '13-indemnification', title: 'Indemnification', icon: ScaleIcon },
    { id: '14-limitation-of-liability', title: 'Limitation of Liability', icon: ExclamationTriangleIcon },
    { id: '15-termination', title: 'Termination', icon: DocumentTextIcon },
    { id: '16-dispute-resolution', title: 'Dispute Resolution', icon: ScaleIcon },
    { id: '17-general-provisions', title: 'General Provisions', icon: DocumentTextIcon },
    { id: '18-contact-information', title: 'Contact Information', icon: EnvelopeIcon }
  ];

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-16">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Terms and Conditions
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              TWENTY FOUR CAPITAL LLC
            </motion.p>
            <motion.div 
              className="mt-4 text-white/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>Effective Date: {currentDate}</p>
              <p>Last Updated: {currentDate}</p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-32 bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                          window.history.pushState(null, '', `#${item.id}`);
                        }
                      }}
                    >
                      <item.icon className="h-4 w-4 text-gray-400" />
                      <span>{item.title}</span>
                    </motion.a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-12">
                {/* Section 1: Introduction and Acceptance */}
                <section id="1-introduction-and-acceptance">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">1.</span> Introduction and Acceptance
                  </h2>
                  
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">1.1 Agreement to Terms</h3>
                      <p className="text-gray-700 leading-relaxed mb-4">
                        These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and Twenty Four Capital LLC, a Wyoming Limited Liability Company ("Company," "we," "us," or "our"), concerning your access to and use of our website, mobile applications, APIs, and all related services (collectively, the "Service").
                      </p>
                      <p className="text-gray-700 leading-relaxed">
                        By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, you do not have permission to access the Service.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">1.2 Description of Company</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Twenty Four Capital LLC operates as a family office providing tech-enabled equity finance solutions. We leverage advanced technology, including artificial intelligence, automated underwriting systems, proprietary algorithms, and data enrichment services to streamline transaction workflows for equity-based financing solutions for nano, micro, and small publicly traded companies.
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">1.3 Regulatory Status</h3>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="font-semibold text-red-800 mb-2">IMPORTANT NOTICE: Twenty Four Capital LLC is NOT:</p>
                        <ul className="list-disc list-inside space-y-1 text-red-700 ml-4">
                          <li>Licensed with the Securities and Exchange Commission (SEC)</li>
                          <li>Licensed with any financial regulators</li>
                          <li>A registered investment advisor</li>
                          <li>A broker-dealer</li>
                          <li>A FINRA member</li>
                          <li>FDIC or SIPC insured</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 2: Eligibility and Access Requirements */}
                <section id="2-eligibility-and-access-requirements">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">2.</span> Eligibility and Access Requirements
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 User Eligibility</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">The Service is exclusively available to:</p>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="font-semibold text-gray-800">a) Accredited Investors</p>
                          <p className="text-gray-700 ml-4">As defined under Rule 501 of Regulation D of the Securities Act of 1933</p>
                        </div>
                        
                        <div>
                          <p className="font-semibold text-gray-800">b) Sophisticated Capital Market Professionals including:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-8">
                            <li>Investment bankers</li>
                            <li>Licensed brokers</li>
                            <li>Individuals representing FINRA member firms</li>
                            <li>Executives or directors of publicly traded companies</li>
                            <li>Corporate consultants acting on behalf of publicly traded companies</li>
                            <li>Other qualified institutional buyers</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Verification Requirements</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">By using our Service, you represent and warrant that you:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Meet the eligibility requirements stated above</li>
                        <li>Are at least 18 years of age</li>
                        <li>Have the legal capacity to enter into these Terms</li>
                        <li>Will provide accurate verification documentation upon request</li>
                        <li>Will immediately notify us if your eligibility status changes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Geographic Restrictions</h3>
                      <p className="text-gray-700 leading-relaxed">
                        The Service is intended for use within the United States. We make no representations that the Service is appropriate or available in other locations. Access from territories where the Service is illegal is prohibited.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 3: Important Disclaimers and Risk Factors */}
                <section id="3-important-disclaimers-and-risk-factors">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">3.</span> Important Disclaimers and Risk Factors
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 No Professional Advice</h3>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="font-semibold text-gray-800 mb-2">THE COMPANY DOES NOT PROVIDE:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li>Investment advice</li>
                          <li>Legal advice</li>
                          <li>Tax advice</li>
                          <li>Financial planning services</li>
                          <li>Securities recommendations</li>
                          <li>Trading recommendations</li>
                        </ul>
                        <p className="text-gray-700 mt-3">
                          All information provided through the Service is for informational purposes only and should not be construed as professional advice.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 No Guarantees</h3>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="font-semibold text-gray-800 mb-2">WE EXPLICITLY DISCLAIM AND YOU ACKNOWLEDGE:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li>No guarantee of performance or returns</li>
                          <li>No guarantee of continued Service operation</li>
                          <li>No guarantee of data accuracy or completeness</li>
                          <li>No guarantee of successful financing outcomes</li>
                          <li>No guarantee of algorithmic accuracy</li>
                          <li>No guarantee against financial loss</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Investment Risks</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Equity-based financing involves substantial risks, including but not limited to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Complete loss of capital</li>
                        <li>Illiquidity of investments</li>
                        <li>Market volatility</li>
                        <li>Regulatory changes</li>
                        <li>Company-specific risks</li>
                        <li>Technology failures</li>
                        <li>Algorithmic errors</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.4 Technology Risks</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Our technology-enabled services are subject to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>System failures or interruptions</li>
                        <li>Cybersecurity breaches</li>
                        <li>Data loss or corruption</li>
                        <li>Algorithm errors or biases</li>
                        <li>Third-party service disruptions</li>
                        <li>API failures or changes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.5 No Insurance Coverage</h3>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="font-semibold text-red-800 mb-2">IMPORTANT: Twenty Four Capital LLC does NOT have:</p>
                        <ul className="list-disc list-inside space-y-1 text-red-700 ml-4">
                          <li>FDIC insurance coverage</li>
                          <li>SIPC insurance coverage</li>
                          <li>Any other governmental insurance or guarantee</li>
                        </ul>
                        <p className="text-red-700 mt-3 font-semibold">
                          Your capital is at risk and is not protected by any insurance scheme.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 4: Account Registration and Security */}
                <section id="4-account-registration-and-security">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">4.</span> Account Registration and Security
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Account Creation</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">To access certain features, you must:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Complete the registration process</li>
                        <li>Provide accurate, current, and complete information</li>
                        <li>Verify your accredited investor or professional status</li>
                        <li>Maintain and update your information</li>
                        <li>Choose a strong, unique password</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Account Security</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">You are responsible for:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Maintaining confidentiality of your account credentials</li>
                        <li>All activities under your account</li>
                        <li>Immediately notifying us of unauthorized use</li>
                        <li>Ensuring your account is not used by others</li>
                        <li>Logging out from shared devices</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Account Suspension</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We reserve the right to suspend or terminate accounts that:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Violate these Terms</li>
                        <li>Provide false information</li>
                        <li>Engage in suspicious activity</li>
                        <li>No longer meet eligibility requirements</li>
                        <li>Pose security risks</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 5: Services Provided */}
                <section id="5-services-provided">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">5.</span> Services Provided
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Technology Platform</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We provide access to our proprietary technology platform featuring:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Automated underwriting systems</li>
                        <li>Transaction processing workflows</li>
                        <li>Data collection and enrichment tools</li>
                        <li>Analytics and reporting dashboards</li>
                        <li>API integrations</li>
                        <li>Document management systems</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Data Services</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Our data services include:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Public company metrics and analytics</li>
                        <li>Market trend analysis</li>
                        <li>Transaction structuring tools</li>
                        <li>Performance benchmarking</li>
                        <li>Risk assessment models</li>
                        <li>Proprietary algorithmic insights</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.3 Financing Solutions</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We facilitate equity-based financing through:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Automated application processing</li>
                        <li>Standardized transaction structures</li>
                        <li>Streamlined documentation</li>
                        <li>Electronic execution capabilities</li>
                        <li>Performance monitoring tools</li>
                        <li>Servicing workflow automation</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">5.4 Service Limitations</h3>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-gray-700 mb-2">Our Service does NOT include:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li>Direct lending or investing</li>
                          <li>Custody of funds or securities</li>
                          <li>Trading or brokerage services</li>
                          <li>Investment management</li>
                          <li>Financial advisory services</li>
                          <li>Legal or tax services</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 6: Prohibited Uses */}
                <section id="6-prohibited-uses">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">6.</span> Prohibited Uses
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 You may not use the Service to:</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Violate any laws or regulations</li>
                        <li>Provide false or misleading information</li>
                        <li>Impersonate others or misrepresent affiliations</li>
                        <li>Access without proper authorization</li>
                        <li>Circumvent security measures</li>
                        <li>Scrape data without permission</li>
                        <li>Interfere with Service operation</li>
                        <li>Transmit malicious code</li>
                        <li>Engage in market manipulation</li>
                        <li>Facilitate money laundering</li>
                        <li>Violate intellectual property rights</li>
                        <li>Harass or harm others</li>
                        <li>Create derivative works without permission</li>
                        <li>Resell or commercialize the Service</li>
                        <li>Use for competitive purposes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Automated Access</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Automated access (including APIs) is only permitted:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>With explicit written authorization</li>
                        <li>In compliance with our API terms</li>
                        <li>Within prescribed rate limits</li>
                        <li>For approved purposes only</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 7: Intellectual Property Rights */}
                <section id="7-intellectual-property-rights">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">7.</span> Intellectual Property Rights
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Company Property</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        All Service content, features, and functionality are owned by Twenty Four Capital LLC, including:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Proprietary algorithms and formulas</li>
                        <li>Underwriting models</li>
                        <li>Software code and applications</li>
                        <li>Data compilations and databases</li>
                        <li>Trademarks and branding</li>
                        <li>Documentation and reports</li>
                        <li>User interface designs</li>
                        <li>Marketing materials</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Limited License</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We grant you a limited, non-exclusive, non-transferable, revocable license to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Access and use the Service for permitted purposes</li>
                        <li>Download authorized reports and documents</li>
                        <li>Use our APIs as permitted</li>
                        <li>Access your account information</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">7.3 Restrictions</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">You may NOT:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Copy, modify, or create derivative works</li>
                        <li>Reverse engineer our technology</li>
                        <li>Remove proprietary notices</li>
                        <li>Use our IP for competitive purposes</li>
                        <li>Sublicense or transfer rights</li>
                        <li>Use our trademarks without permission</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 8: User Content and Data */}
                <section id="8-user-content-and-data">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">8.</span> User Content and Data
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 User Responsibilities</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        You are solely responsible for all content and data you submit, including:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Accuracy and completeness</li>
                        <li>Legal compliance</li>
                        <li>Third-party rights</li>
                        <li>Confidentiality obligations</li>
                        <li>Data security</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.2 License to Company</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Use, process, and analyze your data</li>
                        <li>Provide Services to you</li>
                        <li>Improve our algorithms and models</li>
                        <li>Generate aggregated insights</li>
                        <li>Comply with legal obligations</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">8.3 Data Use Restrictions</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We will NOT:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Sell your personal data</li>
                        <li>Share confidential information without consent</li>
                        <li>Use your data for purposes unrelated to our Services</li>
                        <li>Violate applicable privacy laws</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 9: Third-Party Services and Content */}
                <section id="9-third-party-services-and-content">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">9.</span> Third-Party Services and Content
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 Third-Party Integrations</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Our Service integrates with various third-party services:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Data providers</li>
                        <li>Analytics platforms</li>
                        <li>Communication tools</li>
                        <li>Payment processors</li>
                        <li>Cloud infrastructure</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.2 Third-Party Terms</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Your use of third-party services is governed by their respective terms and policies. We are not responsible for:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Third-party service availability</li>
                        <li>Data accuracy from third parties</li>
                        <li>Third-party security breaches</li>
                        <li>Changes to third-party APIs</li>
                        <li>Third-party service costs</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.3 Links to Other Websites</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Our Service may contain links to third-party websites. We do not control and are not responsible for these external sites.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 10: Fees and Payment Terms */}
                <section id="10-fees-and-payment-terms">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">10.</span> Fees and Payment Terms
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">10.1 Fee Structure</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Fees for our Services are:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Detailed in separate fee schedules</li>
                        <li>Subject to change with notice</li>
                        <li>Based on service tier and usage</li>
                        <li>Exclusive of applicable taxes</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">10.2 Payment Terms</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Payments are due upon invoice</li>
                        <li>Late payments incur interest at 1.5% monthly</li>
                        <li>We may suspend Service for non-payment</li>
                        <li>No refunds for partial periods</li>
                        <li>You are responsible for all collection costs</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">10.3 Taxes</h3>
                      <p className="text-gray-700 leading-relaxed">
                        You are responsible for all applicable taxes, except for our income taxes.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 11: Confidentiality */}
                <section id="11-confidentiality">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">11.</span> Confidentiality
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">11.1 Confidential Information</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Each party agrees to maintain confidentiality of:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Proprietary algorithms and methods</li>
                        <li>Transaction details and terms</li>
                        <li>User data and information</li>
                        <li>Business strategies and plans</li>
                        <li>Technical specifications</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">11.2 Exceptions</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Confidentiality obligations do not apply to information that:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Is publicly available</li>
                        <li>Was rightfully known before disclosure</li>
                        <li>Is independently developed</li>
                        <li>Must be disclosed by law</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">11.3 Duration</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Confidentiality obligations survive termination for five (5) years.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 12: Representations and Warranties */}
                <section id="12-representations-and-warranties">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">12.</span> Representations and Warranties
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">12.1 User Representations</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">You represent and warrant that:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>All information provided is accurate</li>
                        <li>You meet eligibility requirements</li>
                        <li>You have authority to enter these Terms</li>
                        <li>Your use complies with all laws</li>
                        <li>You will not use the Service for illegal purposes</li>
                        <li>You understand the risks involved</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">12.2 Company Disclaimers</h3>
                      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                        <p className="text-gray-800 font-semibold mb-2">
                          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          <li>MERCHANTABILITY</li>
                          <li>FITNESS FOR A PARTICULAR PURPOSE</li>
                          <li>NON-INFRINGEMENT</li>
                          <li>ACCURACY OR COMPLETENESS</li>
                          <li>UNINTERRUPTED OR ERROR-FREE OPERATION</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">12.3 No Warranty on Results</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We do not warrant that:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>The Service will meet your requirements</li>
                        <li>Results will be accurate or reliable</li>
                        <li>Errors will be corrected</li>
                        <li>The Service is free of viruses</li>
                        <li>Financing will be obtained or successful</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 13: Indemnification */}
                <section id="13-indemnification">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">13.</span> Indemnification
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">13.1 User Indemnification</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        You agree to defend, indemnify, and hold harmless Twenty Four Capital LLC, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, and expenses (including attorneys' fees) arising from:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Your use of the Service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any law or third-party rights</li>
                        <li>Your content or data</li>
                        <li>Your negligence or misconduct</li>
                        <li>Your financing transactions</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">13.2 Procedure</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We will notify you of any claim and cooperate in the defense. You may not settle claims without our consent.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 14: Limitation of Liability */}
                <section id="14-limitation-of-liability">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">14.</span> Limitation of Liability
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">14.1 Disclaimer of Damages</h3>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800 font-semibold mb-2">
                          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TWENTY FOUR CAPITAL LLC SHALL NOT BE LIABLE FOR:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-red-700 ml-4">
                          <li>INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES</li>
                          <li>LOSS OF PROFITS, REVENUE, OR BUSINESS</li>
                          <li>LOSS OF DATA OR USE</li>
                          <li>FINANCING TRANSACTION LOSSES</li>
                          <li>INVESTMENT LOSSES</li>
                          <li>ALGORITHM OR SYSTEM ERRORS</li>
                          <li>THIRD-PARTY SERVICE FAILURES</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">14.2 Cap on Liability</h3>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <p className="text-gray-800 font-semibold">
                          OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mt-2">
                          <li>FEES PAID BY YOU IN THE PAST TWELVE MONTHS</li>
                          <li>ONE HUNDRED DOLLARS ($100)</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">14.3 Essential Purpose</h3>
                      <p className="text-gray-700 leading-relaxed">
                        These limitations apply even if remedies fail their essential purpose and regardless of the theory of liability.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 15: Termination */}
                <section id="15-termination">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">15.</span> Termination
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">15.1 Termination by You</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">You may terminate your account at any time by:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Providing written notice</li>
                        <li>Ceasing use of the Service</li>
                        <li>Deleting your account through settings</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">15.2 Termination by Company</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">We may terminate or suspend your access immediately for:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Breach of these Terms</li>
                        <li>Illegal or unauthorized use</li>
                        <li>Non-payment of fees</li>
                        <li>Eligibility changes</li>
                        <li>At our discretion with notice</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">15.3 Effects of Termination</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Upon termination:</p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Access rights cease immediately</li>
                        <li>Licenses granted to you terminate</li>
                        <li>You must cease using all Company IP</li>
                        <li>Outstanding fees become due</li>
                        <li>Certain provisions survive</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">15.4 Survival</h3>
                      <p className="text-gray-700 leading-relaxed">
                        The following sections survive termination: Disclaimers, IP Rights, Confidentiality, Indemnification, Limitation of Liability, and General Provisions.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 16: Dispute Resolution */}
                <section id="16-dispute-resolution">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">16.</span> Dispute Resolution
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">16.1 Arbitration Agreement</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Any dispute arising from these Terms or your use of the Service shall be resolved through binding arbitration under the American Arbitration Association rules, except for:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Small claims court matters</li>
                        <li>Injunctive relief for IP violations</li>
                        <li>Collection of fees owed</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">16.2 Arbitration Process</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Location: Sheridan, Wyoming</li>
                        <li>Arbitrator selection: AAA Commercial Rules</li>
                        <li>Costs: Shared per AAA rules</li>
                        <li>Language: English</li>
                        <li>Decision: Final and binding</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">16.3 Class Action Waiver</h3>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-gray-800 font-semibold">
                          YOU WAIVE ANY RIGHT TO BRING OR PARTICIPATE IN CLASS ACTIONS, COLLECTIVE ACTIONS, OR REPRESENTATIVE PROCEEDINGS.
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">16.4 Time Limitation</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Claims must be brought within one (1) year of arising.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 17: General Provisions */}
                <section id="17-general-provisions">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">17.</span> General Provisions
                  </h2>

                  <div className="space-y-6">
                    {[
                      { title: "17.1 Governing Law", content: "These Terms are governed by Wyoming law, without regard to conflict of law principles." },
                      { title: "17.2 Entire Agreement", content: "These Terms constitute the entire agreement and supersede all prior agreements." },
                      { title: "17.3 Modifications", content: "We may modify these Terms at any time by posting revised terms. Continued use constitutes acceptance." },
                      { title: "17.4 Severability", content: "If any provision is unenforceable, the remainder continues in effect." },
                      { title: "17.5 No Waiver", content: "Our failure to enforce any right is not a waiver of that right." },
                      { title: "17.6 Assignment", content: "You may not assign these Terms without our written consent. We may assign freely." },
                      { title: "17.7 Force Majeure", content: "Neither party is liable for delays due to circumstances beyond reasonable control." }
                    ].map((item, index) => (
                      <div key={index}>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{item.content}</p>
                      </div>
                    ))}

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">17.8 Notices</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">Notices must be in writing to:</p>
                      <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                        <p className="font-semibold">Twenty Four Capital LLC</p>
                        <p>1309 Coffeen Ave Ste 17816</p>
                        <p>SHERIDAN, WY 82801</p>
                        <p>Email: legal@24lp.io</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">17.9 Export Controls</h3>
                      <p className="text-gray-700 leading-relaxed">
                        You comply with all applicable export laws and regulations.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 18: Contact Information */}
                <section id="18-contact-information">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">18.</span> Contact Information
                  </h2>
                  
                  <p className="text-gray-700 leading-relaxed mb-4">
                    For questions about these Terms, contact us at:
                  </p>
                  
                  <div className="bg-orange-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Twenty Four Capital LLC</h3>
                    <p className="text-gray-700 mb-1">1309 Coffeen Ave Ste 17816</p>
                    <p className="text-gray-700 mb-3">Sheridan, WY 82801</p>
                    <p className="text-gray-700 mb-1">Email: legal@24lp.io</p>
                    <p className="text-gray-700">Website: https://24lp.io</p>
                  </div>

                  <div className="bg-gray-100 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      <strong>Version Control:</strong><br />
                      Version: 1.0<br />
                      Effective Date: {currentDate}<br />
                      Last Modified: {currentDate}
                    </p>
                  </div>
                </section>
              </div>
            </main>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default TermsAndConditions; 